import { useCallback } from 'react';
import { useApolloClient } from '@apollo/client';
import {
    ExpertServiceDocument,
    ExpertServiceQuery,
    ExpertServiceQueryVariables,
} from '../../../../../graph/ExpertService.generated';
import { useBriefContext } from '../../../../BriefContext/BriefContext';
import { useUpdateField } from '../../useUpdateField';
import { useBriefSetProductDropdownMutation } from './products.generated';
import { useSelectProductWithCacheUpdate } from './useSelectProductMutation';

export function useUpdateProductDropdown(
    key: string,
    briefId?: string,
    initialValue?: string,
    groupId?: string
) {
    const client = useApolloClient();
    const { isEnrolledInIbpoTest } = useBriefContext();
    const [selectProduct] = useSelectProductWithCacheUpdate();

    const [setProductDropdown] = useBriefSetProductDropdownMutation();

    const updateField = useUpdateField({
        key,
        setField: setProductDropdown,
        briefId,
        initialValue,
    });

    return useCallback(
        async (value: string) => {
            const result = await updateField(value);
            const briefData = result?.data;
            if (!briefData?.setField) {
                return;
            }

            const fields = briefData.setField.dynamicFields.filter((field) => {
                const matchesKey = field.id === key;
                if (!groupId) return matchesKey;
                const fieldGroupId = (field as unknown as { groupId?: string }).groupId;
                return matchesKey && fieldGroupId === groupId;
            });
            if (fields.length === 0) {
                return;
            }

            const correlationId = briefData.setField.correlationId;
            if (!correlationId) {
                return;
            }

            await Promise.all(
                fields.map(async (field) => {
                    if (field.__typename !== 'ClientConfiguredField') {
                        throw new Error(
                            "Field is not a ClientConfiguredField. This schema is not compatible with the fields currently in use on the front end. And will cause all kinds of trouble. If you're seeing this error in bugsnag, investigate immediately"
                        );
                    }
                    const formValue = field.clientConfiguredValue;
                    if (!formValue) {
                        return;
                    }

                    const experiments = isEnrolledInIbpoTest ? { ds_sdp_ibpo: 'test' } : undefined;
                    const { data: esData } = await client.query<
                        ExpertServiceQuery,
                        ExpertServiceQueryVariables
                    >({
                        query: ExpertServiceDocument,
                        variables: { mpvId: formValue, experiments },
                    });

                    const newServiceProductMpvId = esData?.expertService?.serviceProduct?.mpvId;

                    await selectProduct({
                        variables: {
                            requestId: correlationId,
                            input: {
                                printProductMpvId: [formValue],
                                serviceProductMpvId: newServiceProductMpvId,
                            },
                        },
                    });
                })
            );
        },
        [updateField, key, groupId, selectProduct, client, isEnrolledInIbpoTest]
    );
}

export default useUpdateProductDropdown;
